import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Check, X, User, Briefcase, FileText, Banknote,
  ShieldCheck, ShoppingCart, MessageSquare, Send, Package, Tag, Hash,
  BookOpen, ChevronDown, ChevronUp, Plus, Trash2, CheckCircle,
  Wallet, TrendingDown, TrendingUp, BarChart2, AlertTriangle, Download, Calendar,
  Filter, Building2, Users, CheckSquare, Square, MinusSquare
} from 'lucide-react';
import { getAuthHeader } from "../../helpers/auth";

const API = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────
// VOUCHER PDF GENERATOR
// ─────────────────────────────────────────────────────────────
const loadJsPDF = () =>
  new Promise((resolve, reject) => {
    if (window.jspdf) return resolve(window.jspdf.jsPDF);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload  = () => resolve(window.jspdf.jsPDF);
    s.onerror = reject;
    document.head.appendChild(s);
  });

const generateVoucher = async (req, getJobName, getClientName) => {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, mg = 18, lineH = 10;
  let y = 16;
  const tx  = (t, x, yy, opts = {}) => doc.text(String(t ?? ''), x, yy, opts);
  const ln  = (x1, y1, x2, y2)      => doc.line(x1, y1, x2, y2);
  const sf  = (style = 'normal', sz = 11) => { doc.setFont('helvetica', style); doc.setFontSize(sz); };
  doc.setDrawColor(0); doc.setLineWidth(0.5);
  doc.rect(mg - 4, 10, W - (mg - 4) * 2, 272);
  sf('bold', 17);
  tx('PETTY CASH VOUCHER', W / 2, y + 6, { align: 'center' });
  y += 14;
  doc.setLineWidth(0.4); ln(mg, y, W - mg, y); y += 9;
  const voucherNo  = `PCV-${String(req.id).padStart(4, '0')}`;
  const dateStr    = req.requested_at ? new Date(req.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const clientName = getClientName ? getClientName(req.job_order_id, req.schedule_id) : '';
  const jobName    = getJobName(req.job_order_id, req.schedule_id);
  const siteDisplay = clientName && clientName !== jobName ? `${jobName} (${clientName})` : jobName;
  const fields = [
    { label: 'Voucher No:',         value: voucherNo },
    { label: 'Date Requested:',     value: dateStr },
    { label: 'Department (Team):',  value: req.team_id || 'Operations' },
    { label: 'Requested By:',       value: req.requested_by || '—' },
    { label: 'Job / Site Name:',    value: siteDisplay },
    { label: 'Client:',             value: clientName || '—' },
    { label: 'Reason for Request:', value: req.reason || '—' },
  ];
  fields.forEach(({ label, value }) => {
    sf('normal', 11); doc.setTextColor(0);
    tx(label, mg, y);
    const valX = mg + 48;
    tx(value, valX, y);
    doc.setLineWidth(0.25); ln(valX, y + 1.5, W - mg, y + 1.5); y += lineH;
  });
  y += 5;
  const colDesc = mg, colQty = mg + 90, colRate = mg + 118, colAmt = mg + 148, tRight = W - mg, rowH = 11;
  sf('normal', 11);
  tx('Description', colDesc, y); tx('Quantity', colQty, y, { align: 'center' }); tx('Rate', colRate, y, { align: 'center' }); tx('Amount', colAmt, y, { align: 'center' });
  y += 2; doc.setLineWidth(0.4); ln(mg, y, tRight, y); y += 1;
  const rows = [{ d: req.reason || '—', q: '1', r: `Rs.${req.amount}`, a: `Rs.${req.amount}` }, { d: '', q: '', r: '', a: '' }, { d: '', q: '', r: '', a: '' }, { d: '', q: '', r: '', a: '' }];
  rows.forEach(row => {
    doc.setLineWidth(0.3); [colDesc - 1, colQty - 4, colRate - 4, colAmt - 4, tRight].forEach(cx => { ln(cx, y, cx, y + rowH); });
    sf('normal', 10);
    if (row.d) tx(row.d, colDesc + 1, y + 7); if (row.q) tx(row.q, colQty, y + 7, { align: 'center' }); if (row.r) tx(row.r, colRate, y + 7, { align: 'center' }); if (row.a) tx(row.a, colAmt, y + 7, { align: 'center' });
    y += rowH; ln(mg, y, tRight, y);
  });
  y += 7; sf('normal', 11); tx('Total Amount:', colAmt - 2, y, { align: 'right' }); sf('bold', 11); tx(`Rs. ${req.amount}`, colAmt + 2, y); doc.setLineWidth(0.3); ln(colAmt - 4, y + 2, tRight, y + 2);
  y += 22;
  const sigW = 48;
  [{ label: 'Requested By', x: mg }, { label: 'Approved By', x: mg + 60 }, { label: 'Cashier', x: mg + 120 }].forEach(s => { tx(s.label, s.x, y); ln(s.x, y + 10, s.x + sigW, y + 10); });
  sf('normal', 8); doc.setTextColor(140); tx(`Generated on ${new Date().toLocaleString('en-GB')}  |  ${voucherNo}`, W / 2, y + 22, { align: 'center' });
  doc.save(`PettyCash_${voucherNo}.pdf`);
};

const generatePurchaseVoucher = async (req) => {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, mg = 18;
  let y = 16;
  const tx = (t, x, yy, opts = {}) => doc.text(String(t ?? ''), x, yy, opts);
  const ln = (x1, y1, x2, y2)      => doc.line(x1, y1, x2, y2);
  const sf = (style = 'normal', sz = 11) => { doc.setFont('helvetica', style); doc.setFontSize(sz); };
  doc.setDrawColor(0); doc.setLineWidth(0.5); doc.rect(mg - 4, 10, W - (mg - 4) * 2, 272);
  sf('bold', 17); tx('PURCHASE REQUEST VOUCHER', W / 2, y + 6, { align: 'center' }); y += 14;
  doc.setLineWidth(0.4); ln(mg, y, W - mg, y); y += 9;
  const voucherNo = `PRV-${String(req.id).padStart(4, '0')}`;
  const dateStr   = req.requested_at ? new Date(req.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  [{ label: 'Voucher No:', value: voucherNo }, { label: 'Date Requested:', value: dateStr }, { label: 'Department:', value: req.department || 'Operations' }, { label: 'Requested By:', value: req.requested_by || '—' }, { label: 'Vendor Name:', value: req.vendor || '—' }, { label: 'Reason for Request:', value: req.reason || '—' }].forEach(({ label, value }) => {
    sf('normal', 11); doc.setTextColor(0); tx(label, mg, y); const valX = mg + 48; tx(value, valX, y); doc.setLineWidth(0.25); ln(valX, y + 1.5, W - mg, y + 1.5); y += 10;
  });
  y += 5;
  const colDesc = mg, colQty = mg + 90, colRate = mg + 118, colAmt = mg + 148, tRight = W - mg, rowH = 11;
  sf('normal', 11); tx('Description', colDesc, y); tx('Quantity', colQty, y, { align: 'center' }); tx('Rate', colRate, y, { align: 'center' }); tx('Amount', colAmt, y, { align: 'center' });
  y += 2; doc.setLineWidth(0.4); ln(mg, y, tRight, y); y += 1;
  const unitRate = req.quantity && req.estimated_cost ? (parseFloat(req.estimated_cost) / parseFloat(req.quantity)).toFixed(2) : req.estimated_cost;
  [{ d: req.item_name || '—', q: String(req.quantity || 1), r: `Rs.${unitRate}`, a: `Rs.${req.estimated_cost}` }, { d: '', q: '', r: '', a: '' }, { d: '', q: '', r: '', a: '' }, { d: '', q: '', r: '', a: '' }].forEach(row => {
    doc.setLineWidth(0.3); [colDesc - 1, colQty - 4, colRate - 4, colAmt - 4, tRight].forEach(cx => ln(cx, y, cx, y + rowH));
    sf('normal', 10);
    if (row.d) tx(row.d, colDesc + 1, y + 7); if (row.q) tx(row.q, colQty + 7, y + 7, { align: 'center' }); if (row.r) tx(row.r, colRate + 7, y + 7, { align: 'center' }); if (row.a) tx(row.a, colAmt + 7, y + 7, { align: 'center' });
    y += rowH; ln(mg, y, tRight, y);
  });
  if (req.priority) { y += 4; sf('normal', 10); doc.setTextColor(80); tx(`Priority: ${req.priority}`, mg, y); doc.setTextColor(0); }
  y += 6; sf('normal', 11); tx('Total Amount:', colAmt - 2, y, { align: 'right' }); sf('bold', 11); tx(`Rs. ${req.estimated_cost}`, colAmt + 2, y); doc.setLineWidth(0.3); ln(colAmt - 4, y + 2, tRight, y + 2);
  if (req.finance_comment) { y += 10; sf('normal', 9); doc.setTextColor(80); tx(`Finance Note: ${req.finance_comment}`, mg, y); doc.setTextColor(0); }
  y += 20;
  [{ label: 'Requested By', x: mg }, { label: 'Approved By', x: mg + 60 }, { label: 'Cashier', x: mg + 120 }].forEach(s => { tx(s.label, s.x, y); ln(s.x, y + 10, s.x + 48, y + 10); });
  sf('normal', 8); doc.setTextColor(140); tx(`Generated on ${new Date().toLocaleString('en-GB')}  |  ${voucherNo}`, W / 2, y + 22, { align: 'center' });
  doc.save(`Purchase_Voucher_${voucherNo}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────
const statusBadgeStyle = (status) => {
  const map = {
    Approved: { bg: 'rgba(5,150,105,0.1)', color: '#059669', border: 'rgba(5,150,105,0.3)' },
    Rejected: { bg: 'rgba(220,38,38,0.1)', color: '#DC2626', border: 'rgba(220,38,38,0.3)' },
  };
  const s = map[status] || { bg: 'rgba(217,119,6,0.1)', color: '#D97706', border: 'rgba(217,119,6,0.3)' };
  return { padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, letterSpacing: '0.05em', background: s.bg, color: s.color, border: `1px solid ${s.border}` };
};

const priorityColor = (p) => {
  if (p === 'Urgent') return { bg: 'rgba(220,38,38,0.08)', color: '#DC2626', border: 'rgba(220,38,38,0.2)' };
  if (p === 'High')   return { bg: 'rgba(217,119,6,0.08)', color: '#D97706', border: 'rgba(217,119,6,0.2)' };
  return { bg: 'rgba(37,99,235,0.08)', color: '#2563EB', border: 'rgba(37,99,235,0.15)' };
};

const addInputStyle = {
  width: '100%', height: 34, background: '#F9FAFB', border: '1px solid #E5E7EB',
  borderRadius: 7, color: '#111827', fontSize: 12, padding: '0 10px',
  outline: 'none', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box'
};

const addBtnStyle = (color, bg) => ({
  height: 34, padding: '0 12px', borderRadius: 7, background: bg, border: `1px solid ${color}40`,
  color, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
  fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap'
});

// ─────────────────────────────────────────────────────────────
// BULK ACTION BAR
// ─────────────────────────────────────────────────────────────
const BulkActionBar = ({ selected, total, onSelectAll, onDeselectAll, onBulkApprove, onBulkReject, bulkLoading }) => {
  const allSelected = selected.length > 0 && selected.length === total;
  const someSelected = selected.length > 0 && selected.length < total;

  return (
    <div style={{
      background: selected.length > 0 ? '#EFF6FF' : '#fff',
      border: `1px solid ${selected.length > 0 ? 'rgba(37,99,235,0.3)' : '#E5E7EB'}`,
      borderRadius: 12, padding: '12px 18px',
      display: 'flex', alignItems: 'center', gap: 14,
      marginBottom: 14, flexWrap: 'wrap',
      transition: 'all 0.2s',
      boxShadow: selected.length > 0 ? '0 2px 12px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Checkbox */}
      <button
        onClick={allSelected ? onDeselectAll : onSelectAll}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: 0, color: allSelected ? '#2563EB' : someSelected ? '#2563EB' : '#9CA3AF' }}
      >
        {allSelected
          ? <CheckSquare size={20} color="#2563EB" />
          : someSelected
          ? <MinusSquare size={20} color="#2563EB" />
          : <Square size={20} color="#9CA3AF" />}
        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
          {selected.length > 0 ? `${selected.length} selected` : `Select all (${total})`}
        </span>
      </button>

      {selected.length > 0 && (
        <>
          <div style={{ width: 1, height: 24, background: '#E5E7EB' }} />

          {/* Bulk Approve */}
          <button
            onClick={() => onBulkApprove(selected)}
            disabled={bulkLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: bulkLoading ? 'rgba(5,150,105,0.05)' : 'rgba(5,150,105,0.1)',
              border: '1px solid rgba(5,150,105,0.35)',
              borderRadius: 9, padding: '8px 18px',
              color: '#059669', fontSize: 13, fontWeight: 700, cursor: bulkLoading ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {bulkLoading === 'approve'
              ? <><span style={{ width: 14, height: 14, border: '2px solid #059669', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} /> Approving...</>
              : <><Check size={15} /> Approve {selected.length}</>}
          </button>

          {/* Bulk Reject */}
          <button
            onClick={() => onBulkReject(selected)}
            disabled={bulkLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: bulkLoading ? 'rgba(220,38,38,0.04)' : 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderRadius: 9, padding: '8px 18px',
              color: '#DC2626', fontSize: 13, fontWeight: 700, cursor: bulkLoading ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {bulkLoading === 'reject'
              ? <><span style={{ width: 14, height: 14, border: '2px solid #DC2626', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} /> Rejecting...</>
              : <><X size={15} /> Reject {selected.length}</>}
          </button>

          {/* Deselect */}
          <button
            onClick={onDeselectAll}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <X size={13} /> Clear
          </button>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// LEDGER PANEL
// ─────────────────────────────────────────────────────────────
const LedgerPanel = ({ req, onLedgerUpdate }) => {
  const [open, setOpen]       = useState(false);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_received: 0, total_spent: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', amount: '' });
  const [adding, setAdding]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const isApproved = req.status?.toLowerCase() === 'approved';
  useEffect(() => { if (!isApproved || !open) return; fetchLedger(); }, [open, isApproved]);
  if (!isApproved) return null;

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/job-orders/finance/ledger/${req.id}`);
      if (res.data.success) { setEntries(res.data.data.entries); setSummary({ total_received: res.data.data.total_received, total_spent: res.data.data.total_spent, remaining: res.data.data.remaining }); }
    } catch (err) { console.error('Ledger fetch:', err.message); } finally { setLoading(false); }
  };

  const addExpense = async () => {
    if (!newItem.description.trim() || !newItem.amount) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/job-orders/finance/ledger`, { request_id: req.id, description: newItem.description, amount: newItem.amount, added_by: 'finance' });
      if (res.data.success) { setNewItem({ description: '', amount: '' }); setAdding(false); await fetchLedger(); if (onLedgerUpdate) onLedgerUpdate(); }
    } catch { alert('Expense save nahi hua.'); } finally { setSaving(false); }
  };

  const removeExpense = async (id) => {
    try { await axios.delete(`${API}/job-orders/finance/ledger/${id}`); await fetchLedger(); if (onLedgerUpdate) onLedgerUpdate(); } catch { alert('Delete nahi hua.'); }
  };

  const pct = summary.total_received > 0 ? Math.min((summary.total_spent / summary.total_received) * 100, 100) : 0;
  const pctColor = pct > 90 ? '#DC2626' : pct > 65 ? '#D97706' : '#059669';

  return (
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: open ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.25)', borderRadius: 8, padding: '6px 13px', color: '#2563EB', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
        <BookOpen size={13} /> Team Ledger {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {entries.length > 0 && <span style={{ background: 'rgba(37,99,235,0.15)', borderRadius: 4, padding: '1px 7px', fontSize: 10 }}>{entries.length} entries</span>}
        {!open && summary.total_spent > 0 && <span style={{ marginLeft: 4, fontSize: 10, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(summary.total_spent).toLocaleString()} spent</span>}
      </button>
      {open && (
        <div style={{ marginTop: 8, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: '1px solid #E5E7EB' }}>
            {[{ label: 'Approved', val: summary.total_received, color: '#059669', icon: <Wallet size={12} /> }, { label: 'Spent', val: summary.total_spent, color: '#D97706', icon: <TrendingDown size={12} /> }, { label: 'Remaining', val: summary.remaining, color: summary.remaining < 0 ? '#DC2626' : '#2563EB', icon: <BarChart2 size={12} /> }].map(({ label, val, color, icon }, i) => (
              <div key={label} style={{ padding: '10px 14px', borderRight: i < 2 ? '1px solid #E5E7EB' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}><span style={{ color }}>{icon}</span><span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF' }}>{label}</span></div>
                <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(val || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '8px 14px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#9CA3AF', marginBottom: 4 }}><span>Budget utilisation</span><span style={{ color: pctColor, fontWeight: 800 }}>{pct.toFixed(0)}%</span></div>
            <div style={{ height: 5, background: '#E5E7EB', borderRadius: 99 }}><div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 99, transition: 'width 0.4s' }} /></div>
          </div>
          <div style={{ padding: '6px 14px 10px' }}>
            {loading ? <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', padding: '12px 0' }}>Loading...</div>
              : entries.length === 0 && !adding ? <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', padding: '10px 0', fontStyle: 'italic' }}>Abhi tak koi expense nahi.</div>
              : entries.map((entry, i) => (
                <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < entries.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#1F2937', fontWeight: 600 }}>{entry.description}</div>
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>{new Date(entry.added_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}{entry.added_by && <span style={{ marginLeft: 6, color: '#D1D5DB' }}>· {entry.added_by}</span>}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(entry.amount).toLocaleString()}</span>
                    <button onClick={() => removeExpense(entry.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'rgba(220,38,38,0.45)', display: 'flex' }}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            {adding ? (
              <div style={{ marginTop: 8, background: '#fff', borderRadius: 8, padding: 10, border: '1px solid rgba(37,99,235,0.18)' }}>
                <input placeholder="Expense description..." style={addInputStyle} value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} onKeyDown={e => e.key === 'Enter' && addExpense()} autoFocus />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input type="number" placeholder="Amount Rs." style={{ ...addInputStyle, flex: 1, fontFamily: "'DM Mono', monospace" }} value={newItem.amount} onChange={e => setNewItem({ ...newItem, amount: e.target.value })} onKeyDown={e => e.key === 'Enter' && addExpense()} />
                  <button onClick={addExpense} disabled={saving} style={addBtnStyle('#059669', 'rgba(5,150,105,0.1)')}><CheckCircle size={13} /> {saving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => { setAdding(false); setNewItem({ description: '', amount: '' }); }} style={addBtnStyle('#DC2626', 'rgba(220,38,38,0.08)')}><X size={13} /></button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} style={{ marginTop: 8, width: '100%', padding: '7px 0', background: 'rgba(5,150,105,0.04)', border: '1px dashed rgba(5,150,105,0.25)', borderRadius: 8, color: '#059669', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: "'DM Sans', sans-serif" }}>
                <Plus size={12} /> Log Expense
              </button>
            )}
            {summary.remaining < 0 && <div style={{ marginTop: 10, padding: '7px 11px', borderRadius: 7, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', fontSize: 11, color: '#DC2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} /> Overspent by Rs.{Math.abs(summary.remaining).toLocaleString()}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TEAM / JOB FILTER PANEL
// ─────────────────────────────────────────────────────────────
const TeamFilterPanel = ({ pettyCashReqs, getJobName, getClientName, selectedFilter, onFilterChange }) => {
  const [timeRange, setTimeRange] = useState('monthly');
  const [open, setOpen] = useState(false);
  const now = new Date();

  const isInRange = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    if (timeRange === 'weekly') { const pw = new Date(); pw.setDate(now.getDate() - 7); return date >= pw && date <= now; }
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  const approvedReqs = pettyCashReqs.filter(r => r.status?.toLowerCase() === 'approved' && isInRange(r.requested_at));
  const teamMap = {};
  approvedReqs.forEach(r => { const key = r.team_id || 'Unknown'; if (!teamMap[key]) teamMap[key] = { team: key, requests: [], total: 0 }; teamMap[key].requests.push(r); teamMap[key].total += parseFloat(r.amount) || 0; });
  const teams = Object.values(teamMap).sort((a, b) => b.total - a.total);

  const jobMap = {};
  approvedReqs.forEach(r => { const key = r.schedule_id ? `sch-${r.schedule_id}` : `jo-${r.job_order_id}`; const name = getJobName(r.job_order_id, r.schedule_id); const client = getClientName ? getClientName(r.job_order_id, r.schedule_id) : ''; if (!jobMap[key]) jobMap[key] = { key, name, client, job_order_id: r.job_order_id, schedule_id: r.schedule_id, requests: [], total: 0 }; jobMap[key].requests.push(r); jobMap[key].total += parseFloat(r.amount) || 0; });
  const jobs = Object.values(jobMap).sort((a, b) => b.total - a.total);
  const totalAll = approvedReqs.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  let selectedDetail = null;
  if (selectedFilter?.type === 'team') selectedDetail = teams.find(t => t.team === selectedFilter.value);
  else if (selectedFilter?.type === 'job') selectedDetail = jobs.find(j => j.key === selectedFilter.value);

  const handleSelect = (type, value) => {
    if (selectedFilter && selectedFilter.type === type && selectedFilter.value === value) onFilterChange(null);
    else onFilterChange({ type, value });
    setOpen(false);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: selectedFilter ? 14 : 0 }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 7, background: open || selectedFilter ? 'rgba(37,99,235,0.1)' : '#fff', border: `1px solid ${open || selectedFilter ? 'rgba(37,99,235,0.35)' : '#E5E7EB'}`, borderRadius: 10, padding: '8px 16px', color: open || selectedFilter ? '#2563EB' : '#6B7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <Filter size={13} />
          {selectedFilter ? (selectedFilter.type === 'team' ? `Team: ${selectedFilter.value}` : `Job: ${jobs.find(j => j.key === selectedFilter.value)?.name || selectedFilter.value}`) : 'Filter by Team / Job'}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        <div style={{ display: 'flex', background: '#F3F4F6', padding: '3px', borderRadius: 8, border: '1px solid #E5E7EB' }}>
          {[{ id: 'weekly', label: 'This Week' }, { id: 'monthly', label: 'This Month' }].map(f => (
            <button key={f.id} onClick={() => setTimeRange(f.id)} style={{ background: timeRange === f.id ? '#fff' : 'transparent', color: timeRange === f.id ? '#111827' : '#9CA3AF', border: timeRange === f.id ? '1px solid #E5E7EB' : '1px solid transparent', padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', boxShadow: timeRange === f.id ? '0 1px 2px rgba(0,0,0,0.07)' : 'none' }}>{f.label}</button>
          ))}
        </div>
        {selectedFilter && <button onClick={() => onFilterChange(null)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '6px 12px', color: '#DC2626', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}><X size={11} /> Clear Filter</button>}
      </div>
      {open && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', animation: 'fadeIn 0.15s ease', marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ borderRight: '1px solid #F3F4F6' }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 6 }}><Users size={12} color="#2563EB" /><span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF' }}>By Team</span></div>
              <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
                {teams.length === 0 ? <div style={{ padding: '14px 16px', fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>Koi approved request nahi.</div>
                  : teams.map(t => { const isSelected = selectedFilter?.type === 'team' && selectedFilter?.value === t.team; const pct = totalAll > 0 ? (t.total / totalAll) * 100 : 0; return (
                    <div key={t.team} onClick={() => handleSelect('team', t.team)} style={{ padding: '10px 16px', cursor: 'pointer', background: isSelected ? 'rgba(37,99,235,0.07)' : 'transparent', borderLeft: isSelected ? '3px solid #2563EB' : '3px solid transparent', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}><span style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#2563EB' : '#1F2937' }}>{t.team}</span><span style={{ fontSize: 11, fontWeight: 800, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{t.total.toLocaleString()}</span></div>
                      <div style={{ height: 3, background: '#F3F4F6', borderRadius: 99, marginBottom: 4 }}><div style={{ height: '100%', width: `${pct}%`, background: isSelected ? '#2563EB' : '#D97706', borderRadius: 99 }} /></div>
                      <span style={{ fontSize: 10, color: '#9CA3AF' }}>{t.requests.length} request{t.requests.length > 1 ? 's' : ''}</span>
                    </div>
                  ); })}
              </div>
            </div>
            <div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={12} color="#059669" /><span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF' }}>By Job / Site</span></div>
              <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
                {jobs.length === 0 ? <div style={{ padding: '14px 16px', fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>Koi approved request nahi.</div>
                  : jobs.map(j => { const isSelected = selectedFilter?.type === 'job' && selectedFilter?.value === j.key; const pct = totalAll > 0 ? (j.total / totalAll) * 100 : 0; return (
                    <div key={j.key} onClick={() => handleSelect('job', j.key)} style={{ padding: '10px 16px', cursor: 'pointer', background: isSelected ? 'rgba(5,150,105,0.06)' : 'transparent', borderLeft: isSelected ? '3px solid #059669' : '3px solid transparent', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}><span style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#059669' : '#1F2937' }}>{j.name}</span><span style={{ fontSize: 11, fontWeight: 800, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{j.total.toLocaleString()}</span></div>
                      {j.client && j.client !== j.name && <div style={{ fontSize: 10, color: '#6B7280', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 3 }}><Building2 size={9} /> {j.client}</div>}
                      <div style={{ height: 3, background: '#F3F4F6', borderRadius: 99, marginBottom: 4 }}><div style={{ height: '100%', width: `${pct}%`, background: isSelected ? '#059669' : '#D97706', borderRadius: 99 }} /></div>
                      <span style={{ fontSize: 10, color: '#9CA3AF' }}>{j.requests.length} request{j.requests.length > 1 ? 's' : ''}</span>
                    </div>
                  ); })}
              </div>
            </div>
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid #F3F4F6', background: '#F9FAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#9CA3AF' }}>Total approved {timeRange === 'weekly' ? 'this week' : 'this month'}</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: '#059669', fontFamily: "'DM Mono', monospace" }}>Rs.{totalAll.toLocaleString()}</span>
          </div>
        </div>
      )}
      {selectedFilter && selectedDetail && (
        <div style={{ background: '#fff', border: `1px solid ${selectedFilter.type === 'team' ? 'rgba(37,99,235,0.2)' : 'rgba(5,150,105,0.2)'}`, borderRadius: 12, overflow: 'hidden', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: selectedFilter.type === 'team' ? 'rgba(37,99,235,0.03)' : 'rgba(5,150,105,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {selectedFilter.type === 'team' ? <Users size={14} color="#2563EB" /> : <Building2 size={14} color="#059669" />}
              <div><div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{selectedFilter.type === 'team' ? selectedDetail.team : selectedDetail.name}</div>
                {selectedFilter.type === 'job' && selectedDetail.client && selectedDetail.client !== selectedDetail.name && <div style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}><Building2 size={9} /> Client: {selectedDetail.client}</div>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 18, fontWeight: 900, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{selectedDetail.total.toLocaleString()}</div><div style={{ fontSize: 10, color: '#9CA3AF' }}>{selectedDetail.requests.length} requests</div></div>
          </div>
          <div style={{ padding: '8px 18px 12px' }}>
            {selectedDetail.requests.map((r, i) => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < selectedDetail.requests.length - 1 ? '1px solid #F9FAFB' : 'none' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937' }}>{selectedFilter.type === 'team' ? getJobName(r.job_order_id, r.schedule_id) : (r.requested_by || r.team_id || '—')}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>{r.reason || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{parseFloat(r.amount).toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{new Date(r.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// FINANCE SUMMARY
// ─────────────────────────────────────────────────────────────
const FinanceSummary = ({ pettyCashReqs, ledgerTrigger }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [allExpenses, setAllExpenses] = useState([]);
  const now = new Date();

  const filterByTime = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    if (timeFilter === 'daily') return date.toDateString() === now.toDateString();
    if (timeFilter === 'weekly') { const pw = new Date(); pw.setDate(now.getDate() - 7); return date >= pw && date <= now; }
    if (timeFilter === 'monthly') return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    return true;
  };

  useEffect(() => {
    const fetchAllLedgers = async () => {
      const approvedReqs = pettyCashReqs.filter(r => r.status?.toLowerCase() === 'approved');
      if (approvedReqs.length === 0) { setAllExpenses([]); return; }
      let expensesList = [];
      await Promise.all(approvedReqs.map(r => axios.get(`${API}/job-orders/finance/ledger/${r.id}`).then(res => { if (res.data.success && res.data.data.entries) expensesList = [...expensesList, ...res.data.data.entries]; }).catch(() => {})));
      setAllExpenses(expensesList);
    };
    fetchAllLedgers();
  }, [pettyCashReqs, ledgerTrigger]);

  const filteredCashReqs = pettyCashReqs.filter(r => r.status?.toLowerCase() === 'approved' && filterByTime(r.requested_at));
  const filteredExpenses = allExpenses.filter(e => filterByTime(e.added_at));
  const totalDiya = filteredCashReqs.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalUseHua = filteredExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const totalRemaining = totalDiya - totalUseHua;
  const pct = totalDiya > 0 ? Math.min((totalUseHua / totalDiya) * 100, 100) : 0;
  const pctColor = pct > 90 ? '#DC2626' : pct > 65 ? '#D97706' : '#059669';

  return (
    <div style={{ marginBottom: 28, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ padding: '14px 22px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 15 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Calendar size={16} color="#2563EB" /></div>
          <div><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: '#9CA3AF', textTransform: 'uppercase' }}>Financial Overview</div><div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>Petty Cash Summary</div></div>
        </div>
        <div style={{ display: 'flex', background: '#F3F4F6', padding: '4px', borderRadius: 10, border: '1px solid #E5E7EB' }}>
          {[{ id: 'daily', label: 'Daily' }, { id: 'weekly', label: 'Weekly' }, { id: 'monthly', label: 'Monthly' }].map(f => (
            <button key={f.id} onClick={() => setTimeFilter(f.id)} style={{ background: timeFilter === f.id ? '#fff' : 'transparent', color: timeFilter === f.id ? '#111827' : '#9CA3AF', border: timeFilter === f.id ? '1px solid #E5E7EB' : '1px solid transparent', padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', boxShadow: timeFilter === f.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{f.label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {[{ label: 'Total Sent (Approved)', val: totalDiya, color: '#059669', icon: <Wallet size={15} />, sub: `${filteredCashReqs.length} requests` }, { label: 'Total Use (Spent)', val: totalUseHua, color: '#D97706', icon: <TrendingDown size={15} />, sub: `Budget Used: ${pct.toFixed(0)}%` }, { label: 'Total Remaining', val: totalRemaining, color: totalRemaining < 0 ? '#DC2626' : '#2563EB', icon: <BarChart2 size={15} />, sub: 'Cash Flow Balance' }].map(({ label, val, color, icon, sub }, i) => (
          <div key={label} style={{ padding: '16px 20px', borderRight: i < 2 ? '1px solid #F3F4F6' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}><span style={{ color }}>{icon}</span><span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF' }}>{label}</span></div>
            <div style={{ fontSize: 20, fontWeight: 900, color, fontFamily: "'DM Mono', monospace", marginBottom: 2 }}>Rs.{val.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: '#9CA3AF' }}>{sub}</div>
            {label.includes('Use') && <div style={{ height: 4, background: '#F3F4F6', borderRadius: 99, marginTop: 8 }}><div style={{ height: '100%', width: `${pct}%`, background: pctColor, borderRadius: 99, transition: 'width 0.4s' }} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PETTY CASH CARD  (with checkbox)
// ─────────────────────────────────────────────────────────────
const PettyCashCard = ({ req, getJobName, getClientName, formatTime, onStatus, onLedgerUpdate, selected, onToggleSelect }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try { await generateVoucher(req, getJobName, getClientName); }
    catch (e) { console.error(e); alert('Voucher generate nahi hua.'); }
    finally { setDownloading(false); }
  };

  const jobName    = getJobName(req.job_order_id, req.schedule_id);
  const clientName = getClientName ? getClientName(req.job_order_id, req.schedule_id) : '';
  const showClient = clientName && clientName !== jobName;

  return (
    <div style={{
      background: '#fff', border: `1px solid ${selected ? 'rgba(37,99,235,0.4)' : '#E5E7EB'}`,
      borderRadius: '16px', padding: '20px 24px',
      boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {/* Top row with checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: showClient ? 4 : 8, flexWrap: 'wrap' }}>

            {/* Checkbox — only for pending */}
            {req.status === 'Pending' && (
              <button
                onClick={() => onToggleSelect(req.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                {selected
                  ? <CheckSquare size={18} color="#2563EB" />
                  : <Square size={18} color="#CBD5E1" />}
              </button>
            )}

            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={14} color="#2563EB" /> {jobName}
            </div>
            <span style={{ fontSize: 11, color: '#9CA3AF', fontFamily: "'DM Mono', monospace" }}>
              {req.schedule_id ? `#SCH-${req.schedule_id}` : `#JO-${req.job_order_id}`}
            </span>
            {req.schedule_id && <span style={{ background: 'rgba(37,99,235,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: 9, color: '#2563EB' }}>Commercial</span>}
          </div>

          {showClient && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
              <Building2 size={11} color="#6B7280" />
              <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Client:</span>
              <span style={{ fontSize: 12, color: '#2563EB', fontWeight: 700 }}>{clientName}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 15, fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={12} /> {req.team_id}{req.requested_by && <span style={{ color: '#059669', marginLeft: 4 }}>(By: {req.requested_by})</span>}</div>
            <div style={{ color: '#9CA3AF' }}>{formatTime(req.requested_at)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, background: '#F9FAFB', padding: '10px 14px', borderRadius: 8, border: '1px solid #F3F4F6' }}>
            <FileText size={14} color="#9CA3AF" style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontStyle: req.reason ? 'normal' : 'italic', color: req.reason ? '#374151' : '#9CA3AF' }}>{req.reason || 'No details provided.'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 30, minWidth: 170, gap: 8 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{req.amount}</div>
          {req.status === 'Pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onStatus(req.id, 'Rejected')} style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#DC2626', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}><X size={14} /> Reject</button>
              <button onClick={() => onStatus(req.id, 'Approved')} style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.25)', color: '#059669', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}><Check size={14} /> Approve</button>
            </div>
          ) : (
            <div style={statusBadgeStyle(req.status)}>{req.status.toUpperCase()}</div>
          )}
          {req.status === 'Approved' && (
            <button onClick={handleDownload} disabled={downloading} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: downloading ? 'rgba(217,119,6,0.04)' : 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 9, padding: '9px 0', color: '#D97706', fontSize: 12, fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s' }}>
              <Download size={14} />{downloading ? 'Generating PDF...' : 'Download Voucher'}
            </button>
          )}
        </div>
      </div>
      <LedgerPanel req={req} onLedgerUpdate={onLedgerUpdate} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PURCHASE CARD  (with checkbox)
// ─────────────────────────────────────────────────────────────
const PurchaseCard = ({ req, formatTime, onStatus, onComment, selected, onToggleSelect }) => {
  const [commentText, setCommentText] = useState(req.finance_comment || '');
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pc = priorityColor(req.priority);

  const handleDownload = async () => {
    setDownloading(true);
    try { await generatePurchaseVoucher(req); }
    catch (e) { console.error(e); alert('Voucher generate nahi hua.'); }
    finally { setDownloading(false); }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(req.id, commentText);
    setShowComment(false);
    setSubmitting(false);
  };

  return (
    <div style={{
      background: '#fff', border: `1px solid ${selected ? 'rgba(37,99,235,0.4)' : '#E5E7EB'}`,
      borderRadius: '16px', padding: '20px 24px',
      boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            {/* Checkbox — only for pending */}
            {req.status === 'Pending' && (
              <button onClick={() => onToggleSelect(req.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {selected ? <CheckSquare size={18} color="#2563EB" /> : <Square size={18} color="#CBD5E1" />}
              </button>
            )}
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}><Package size={14} color="#D97706" /> {req.item_name || 'Unnamed Item'}</div>
            <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>{req.priority || 'NORMAL'}</span>
          </div>
          <div style={{ display: 'flex', gap: 15, fontSize: 12, color: '#6B7280', marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Hash size={12} /> Qty: {req.quantity}</div>
            {req.vendor && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Tag size={12} /> {req.vendor}</div>}
            {req.requested_by && <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#059669' }}><User size={12} /> By: {req.requested_by}</div>}
            <div style={{ color: '#9CA3AF' }}>{formatTime(req.requested_at)}</div>
          </div>
          {req.reason && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, background: '#F9FAFB', padding: '10px 14px', borderRadius: 8, border: '1px solid #F3F4F6', marginBottom: 10 }}><FileText size={14} color="#9CA3AF" style={{ marginTop: 2, flexShrink: 0 }} /><span style={{ color: '#374151' }}>{req.reason}</span></div>}
          {req.finance_comment && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', fontSize: 12, color: '#2563EB', display: 'flex', gap: 8, alignItems: 'flex-start' }}><MessageSquare size={13} style={{ marginTop: 2, flexShrink: 0 }} /><div><span style={{ fontWeight: 700, marginRight: 6 }}>Finance note:</span>{req.finance_comment}</div></div>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 30, minWidth: 150, gap: 8 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#D97706', fontFamily: "'DM Mono', monospace" }}>Rs.{req.estimated_cost}</div>
          {req.status === 'Pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => onStatus(req.id, 'Rejected')} style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: '#DC2626', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}><X size={13} /> Reject</button>
              <button onClick={() => onStatus(req.id, 'Approved')} style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.25)', color: '#059669', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}><Check size={13} /> Approve</button>
            </div>
          ) : (
            <div style={statusBadgeStyle(req.status)}>{req.status.toUpperCase()}</div>
          )}
          {req.status === 'Approved' && <button onClick={handleDownload} disabled={downloading} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: downloading ? 'rgba(217,119,6,0.04)' : 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 9, padding: '9px 0', color: '#D97706', fontSize: 12, fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s' }}><Download size={14} />{downloading ? 'Generating PDF...' : 'Download Voucher'}</button>}
          <button onClick={() => setShowComment(!showComment)} style={{ background: showComment ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.2)', color: '#2563EB', padding: '7px 13px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700 }}><MessageSquare size={12} /> {req.finance_comment ? 'Edit Comment' : 'Add Comment'}</button>
        </div>
      </div>

      {showComment && (
        <div style={{ marginTop: 14, padding: 14, background: '#F9FAFB', borderRadius: 10, border: '1px solid rgba(37,99,235,0.15)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Finance Comment</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <textarea placeholder="Add a note for the operations team..." value={commentText} onChange={(e) => setCommentText(e.target.value)} style={{ flex: 1, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, color: '#111827', fontSize: 13, padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: 60, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }} />
            <button onClick={handleCommentSubmit} disabled={submitting || !commentText.trim()} style={{ background: submitting || !commentText.trim() ? '#BFDBFE' : '#2563EB', border: 'none', color: '#fff', borderRadius: 8, padding: '0 16px', cursor: submitting || !commentText.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, alignSelf: 'flex-end', height: 38 }}><Send size={13} /> {submitting ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────
const FinanceDashboard = () => {
  const [activeTab, setActiveTab]           = useState('petty-cash');
  const [pettyCashReqs, setPettyCashReqs]   = useState([]);
  const [purchaseReqs, setPurchaseReqs]     = useState([]);
  const [jobOrders, setJobOrders]           = useState([]);
  const [commercialJobs, setCommercialJobs] = useState([]);
  const [statusFilter, setStatusFilter]     = useState('Pending');
  const [loading, setLoading]               = useState(true);
  const [ledgerTrigger, setLedgerTrigger]   = useState(0);
  const [activeTeamJobFilter, setActiveTeamJobFilter] = useState(null);

  // ── Bulk selection state ──
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(null); // 'approve' | 'reject' | null

  useEffect(() => {
    // Clear selections when tab or filter changes
    setSelectedIds([]);
  }, [activeTab, statusFilter, activeTeamJobFilter]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        let jobRes;
        try { jobRes = await axios.get(`${API}/job-orders/ops-job-orders`); }
        catch { jobRes = await axios.get(`${API}/active-dropdown`); }
        const raw = jobRes.data;
        let jobs = [];
        if (raw && Array.isArray(raw.data)) jobs = raw.data;
        else if (raw && Array.isArray(raw.rows)) jobs = raw.rows;
        else if (Array.isArray(raw)) jobs = raw;
        setJobOrders(jobs);
      } catch (e) { console.error('Job Orders error:', e); }

      try {
        const config = getAuthHeader();
        const storedUser = localStorage.getItem("user");
        let params = {};
        if (storedUser) { const parsedUser = JSON.parse(storedUser); const clientId = parsedUser.client_id || parsedUser.id; if (clientId) params.client_id = clientId; }
        const commRes = await axios.get(`${API}/schedule/history`, { params, ...config });
        if (commRes.data && Array.isArray(commRes.data.jobs)) setCommercialJobs(commRes.data.jobs);
      } catch (e) { console.error('Commercial jobs fetch error:', e); }

      try { const res = await axios.get(`${API}/job-orders/finance/petty-cash-all`); setPettyCashReqs(res.data.data || []); }
      catch (e) { console.error('Petty cash error:', e); }

      try { const res = await axios.get(`${API}/purchase-request/get-requests`); setPurchaseReqs(res.data.data || []); }
      catch (e) { console.error('Purchase requests error:', e); }

      setLoading(false);
    };
    fetchAll();
  }, []);

  const handlePettyCashStatus = async (id, status) => {
    try { await axios.post(`${API}/job-orders/finance/petty-cash-status`, { id, status }); setPettyCashReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r)); }
    catch { alert('Failed to update status.'); }
  };

  const handlePurchaseStatus = async (id, status) => {
    try { await axios.post(`${API}/purchase-request/set-approval`, { id, status }); setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r)); }
    catch { alert('Failed to update status.'); }
  };

  const handlePurchaseComment = async (id, comment) => {
    try { await axios.post(`${API}/puchase-request/set-comment`, { id, comment }); setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, finance_comment: comment } : r)); }
    catch { alert('Failed to save comment.'); }
  };

  // ── Bulk Actions ──
  const handleBulkAction = async (ids, status) => {
    const actionKey = status === 'Approved' ? 'approve' : 'reject';
    setBulkLoading(actionKey);
    try {
      const endpoint = activeTab === 'petty-cash'
        ? `${API}/job-orders/finance/petty-cash-status`
        : `${API}/purchase-request/set-approval`;

      // Run all requests in parallel
      await Promise.all(ids.map(id => axios.post(endpoint, { id, status })));

      // Update local state
      if (activeTab === 'petty-cash') {
        setPettyCashReqs(prev => prev.map(r => ids.includes(r.id) ? { ...r, status } : r));
      } else {
        setPurchaseReqs(prev => prev.map(r => ids.includes(r.id) ? { ...r, status } : r));
      }
      setSelectedIds([]);
    } catch {
      alert(`Bulk ${status} failed. Please try again.`);
    } finally {
      setBulkLoading(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const getJobName = (jobId, scheduleId) => {
    if (scheduleId) { const job = commercialJobs.find(j => (j.schedule_id || j.id) == scheduleId); return job ? (job.site_name || job.client_name || `Schedule-${scheduleId}`) : `Schedule-${scheduleId}`; }
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    return j ? (j.customer_name || j.client_name || `JO-${jobId}`) : `JO-${jobId}`;
  };

  const getClientName = (jobId, scheduleId) => {
    if (scheduleId) { const job = commercialJobs.find(j => (j.schedule_id || j.id) == scheduleId); if (!job) return ''; return job.client_name || ''; }
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    if (!j) return '';
    return j.client_name || j.customer_name || '';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const applyFilters = (reqs) => {
    let list = reqs.filter(r => statusFilter === 'All' || r.status === statusFilter);
    if (activeTeamJobFilter) {
      if (activeTeamJobFilter.type === 'team') list = list.filter(r => (r.team_id || 'Unknown') === activeTeamJobFilter.value);
      else if (activeTeamJobFilter.type === 'job') { const key = activeTeamJobFilter.value; list = list.filter(r => { const rKey = r.schedule_id ? `sch-${r.schedule_id}` : `jo-${r.job_order_id}`; return rKey === key; }); }
    }
    return list;
  };

  const filteredPetty    = applyFilters(pettyCashReqs);
  const filteredPurchase = applyFilters(purchaseReqs);
  const currentList      = activeTab === 'petty-cash' ? filteredPetty : filteredPurchase;
  // Only pending items can be bulk-selected
  const pendingInView    = currentList.filter(r => r.status === 'Pending');
  const pendingPetty     = pettyCashReqs.filter(r => r.status === 'Pending').length;
  const pendingPurchase  = purchaseReqs.filter(r => r.status === 'Pending').length;

  return (
    <div style={{ padding: '40px 20px', background: '#F3F4F6', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.12);border-radius:99px}
      `}</style>

      <div style={{ maxWidth: '980px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#2563EB', letterSpacing: '0.1em', marginBottom: 4 }}>FINANCE DEPARTMENT</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={28} color="#059669" /> Finance Approvals
          </h1>
        </div>

        <FinanceSummary pettyCashReqs={pettyCashReqs} ledgerTrigger={ledgerTrigger} />

        {/* Tabs + Status Filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'petty-cash', label: 'Petty Cash',        icon: <Banknote size={15} />,    pending: pendingPetty,    color: '#059669' },
            { key: 'purchase',   label: 'Purchase Requests', icon: <ShoppingCart size={15} />, pending: pendingPurchase, color: '#D97706' },
          ].map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setStatusFilter('Pending'); setActiveTeamJobFilter(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12, background: activeTab === tab.key ? '#fff' : 'transparent', border: activeTab === tab.key ? `1px solid ${tab.color}30` : '1px solid #E5E7EB', color: activeTab === tab.key ? '#111827' : '#9CA3AF', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.07)' : 'none' }}>
              <span style={{ color: activeTab === tab.key ? tab.color : '#D1D5DB' }}>{tab.icon}</span>
              {tab.label}
              {tab.pending > 0 && <span style={{ background: tab.color, color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>{tab.pending}</span>}
            </button>
          ))}

          <div style={{ marginLeft: 'auto', display: 'flex', background: '#fff', padding: '6px', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            {['Pending', 'Approved', 'Rejected', 'All'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)} style={{ background: statusFilter === f ? '#F3F4F6' : 'transparent', color: statusFilter === f ? '#111827' : '#9CA3AF', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{f}</button>
            ))}
          </div>
        </div>

        {/* Team / Job Filter — only petty cash */}
        {activeTab === 'petty-cash' && (
          <TeamFilterPanel pettyCashReqs={pettyCashReqs} getJobName={getJobName} getClientName={getClientName} selectedFilter={activeTeamJobFilter} onFilterChange={setActiveTeamJobFilter} />
        )}

        {/* ── BULK ACTION BAR — only shown when statusFilter is Pending ── */}
        {!loading && statusFilter === 'Pending' && pendingInView.length > 0 && (
          <BulkActionBar
            selected={selectedIds}
            total={pendingInView.length}
            onSelectAll={() => setSelectedIds(pendingInView.map(r => r.id))}
            onDeselectAll={() => setSelectedIds([])}
            onBulkApprove={(ids) => handleBulkAction(ids, 'Approved')}
            onBulkReject={(ids) => handleBulkAction(ids, 'Rejected')}
            bulkLoading={bulkLoading}
          />
        )}

        {/* Cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#9CA3AF' }}>Loading requests...</div>
        ) : currentList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 16, border: '1px dashed #E5E7EB' }}>
            {activeTab === 'petty-cash' ? <Banknote size={40} color="#E5E7EB" style={{ marginBottom: 15 }} /> : <ShoppingCart size={40} color="#E5E7EB" style={{ marginBottom: 15 }} />}
            <div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>No {statusFilter} {activeTab === 'petty-cash' ? 'Petty Cash' : 'Purchase'} Requests</div>
            {activeTeamJobFilter && <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 6 }}>Try clearing the Team/Job filter above.</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {activeTab === 'petty-cash'
              ? filteredPetty.map(req =>
                  <PettyCashCard
                    key={req.id} req={req}
                    getJobName={getJobName} getClientName={getClientName}
                    formatTime={formatTime}
                    onStatus={handlePettyCashStatus}
                    onLedgerUpdate={() => setLedgerTrigger(t => t + 1)}
                    selected={selectedIds.includes(req.id)}
                    onToggleSelect={toggleSelect}
                  />
                )
              : filteredPurchase.map(req =>
                  <PurchaseCard
                    key={req.id} req={req}
                    formatTime={formatTime}
                    onStatus={handlePurchaseStatus}
                    onComment={handlePurchaseComment}
                    selected={selectedIds.includes(req.id)}
                    onToggleSelect={toggleSelect}
                  />
                )
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceDashboard;